import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import _ from "lodash";

// Redux Actions
import {
  getProduct,
  getTerritoryForHOUser,
} from "@redux/actions/utility-actions";
import { getClusterCode } from "@redux/actions/zsm-action";
import {
  getSecondarySalesAcheived,
  getSecondarySalesAcheivedTrend,
  getSecondarySalesTargetAcheived,
  getSecondarySalesTargetAcheivedTrend,
} from "@redux/actions/secondarySales-actions";
import {
  getTeamsAverageTsiScore,
  getTeamsAverageTsrScore,
  getTeamsIncentive,
  getTeamsSop,
} from "@redux/actions/teams-action";
import {
  getWssAutoVAlueFtd,
  getWssAutoValueFtdTrend,
  getWssCodeForHORDM,
  getWssDarpanSyncDaysException,
  getWssWssInBlock,
} from "@redux/actions/wss-actions";
import {
  getOutletsBilledFtm,
  getOutletsBilledFtmTrend,
  getRsmFtm,
  getRsmFtmTrend,
} from "@redux/actions/salesOperation-action";
import { getHoNsmEmail } from "@redux/actions/hoUser-action";
import { SaveSelectedSalesGroupCode } from "@redux/actions/saveSelectedSalesGroup-action";

// Services
import { getSalesCodeApi } from "@services/DivisonGroup/getSalesCodeApi";
import { getSH7CodeHoUserApi } from "@services/hoUser/getSH7CodeHoUserApi";

// Components
import ModalPopup from "@components/Common/ModalPopup";
import Loader from "@features/shared/components/Loader";

// Utils
import { getUniqueListBy } from "@components/Common/Function";

/**
 * DivisionGroup Component
 * Modal for HO users to select Division and Sales Group codes
 * Refactored with Tailwind CSS
 * 
 * @component
 */
const DivisionGroup = ({ setIsDivisionGroupClicked }) => {
  const dispatch = useDispatch();

  // Redux State
  const divisionCode = useSelector((state) =>
    getUniqueListBy(state?.divisionGroup?.getDivisionGroup, "division")
  );
  const userName = useSelector((state) => state?.userDetails?.details?.Name);
  const userType = useSelector((state) =>
    state?.userType?.type !== "Other"
      ? state?.userType?.type
      : state?.userDetails?.details?.Title
  );
  const userDivision = useSelector(
    (state) => state?.userDetails?.details?.Division
  );

  // Local State
  const [isModalOpen, setIsModalOpen] = useState(true);
  const [groupData, setGroupData] = useState({
    division: "",
    sales: "",
  });
  const [formValidation, setFormValidation] = useState({});
  const [selectedDivisionCode, setSelectedDivisionCode] = useState(0);
  const [isSubmit, setIsSubmit] = useState(false);
  const [salesCode, setSalesCode] = useState([]);
  const [loading, setLoading] = useState(false);

  // Handle input change
  const onChange = (e) => {
    setGroupData({ ...groupData, [e.target.name]: e.target.value });
    if (e.target?.name === "division") {
      setSelectedDivisionCode(e.target?.value);
    }
  };

  // True when the user's profile already has a usable division pre-set
  // (divisions 70/30 always, OR any division for TMM users)
  const isDivisionPreset =
    userDivision == 70 ||
    userDivision == 30 ||
    (userType === "TMM" && !!userDivision);

  // Fetch sales codes based on division
  useEffect(() => {
    if (
      !_.isEmpty(selectedDivisionCode) ||
      isDivisionPreset
    ) {
      (async () => {
        const divisionCode =
          isDivisionPreset
            ? parseInt(userDivision, 10)
            : selectedDivisionCode;
        const response = await getSalesCodeApi(divisionCode);
        if (response?.status === 200) {
          const FilterCodes = getUniqueListBy(
            response?.data,
            "sales_group_code_c"
          );
          setSalesCode(FilterCodes);
        }
      })();
    }
  }, [selectedDivisionCode, userDivision]);

  // Handle modal close
  const handleClose = () => setIsModalOpen(false);

  // Handle form submit
  const onSubmit = (e) => {
    e.preventDefault();
    setIsSubmit(true);
    setLoading(true);
    setFormValidation(checkValidation(groupData));
  };

  // Validation check
  const checkValidation = (values) => {
    const errors = {};
    if (!values.division && !isDivisionPreset) {
      errors.division = "Division group is required";
    }
    if (!values.sales) {
      errors.sales = "Sales group is required";
    }
    return errors;
  };

  // Get Dashboard queries
  const getDashboardQueries = React.useCallback((SH_Code__c, Title) => {
    dispatch(getSecondarySalesAcheived(SH_Code__c, Title));
    dispatch(getSecondarySalesAcheivedTrend(SH_Code__c, Title));
    dispatch(getSecondarySalesTargetAcheived(SH_Code__c, Title));
    dispatch(getSecondarySalesTargetAcheivedTrend(SH_Code__c, Title));
    dispatch(getTeamsAverageTsiScore(SH_Code__c, Title));
    dispatch(getTeamsAverageTsrScore(SH_Code__c, Title));
    dispatch(getTeamsIncentive(SH_Code__c, Title));
    dispatch(getTeamsSop(SH_Code__c, Title));
    dispatch(getWssAutoVAlueFtd(SH_Code__c, Title));
    dispatch(getWssAutoValueFtdTrend(SH_Code__c, Title));
    dispatch(getWssWssInBlock());
    dispatch(getWssDarpanSyncDaysException(SH_Code__c, Title));
    dispatch(getOutletsBilledFtm(SH_Code__c, Title));
    dispatch(getOutletsBilledFtmTrend(SH_Code__c, Title));
    dispatch(getRsmFtm(SH_Code__c, Title));
    dispatch(getRsmFtmTrend(SH_Code__c, Title));
  }, [dispatch]);

  // Handle territory API call after validation
  useEffect(() => {
    if (
      Object?.keys(formValidation)?.length === 0 &&
      isSubmit &&
      (groupData?.division || isDivisionPreset)
    ) {
      (async () => {
        // Reset isSubmit immediately so re-renders triggered by groupData/state
        // resets below do not re-fire this effect (especially when isDivisionPreset
        // keeps the condition permanently true for TMM users).
        setIsSubmit(false);

        const data = {
          divisionGroup:
            isDivisionPreset
              ? userDivision
              : groupData.division,
          salesGroup: groupData.sales,
        };

        const { divisionGroup, salesGroup } = data;
        dispatch(SaveSelectedSalesGroupCode(salesGroup));
        const res = await getSH7CodeHoUserApi(divisionGroup, salesGroup);
        const clusterRes = await dispatch(
          getClusterCode(res, userType, divisionGroup, salesGroup)
        );
        dispatch(getWssCodeForHORDM(clusterRes));
        dispatch(getProduct(divisionGroup, salesGroup));
        dispatch(getHoNsmEmail(salesGroup));
        getDashboardQueries(res, userType);
        await dispatch(
          getTerritoryForHOUser({
            divisionGroup,
            salesGroup,
          })
        );
        setLoading(false);
        setIsModalOpen(false);
        setGroupData({
          division: "",
          sales: "",
        });
        // Always notify parent that the modal is done so it can reset showModal.
        // This is critical for impersonation: if showModal stays true in the parent,
        // switching back to the original user won't trigger a remount of this component.
        if (typeof setIsDivisionGroupClicked === "function") {
          setIsDivisionGroupClicked(false);
        }
      })();
    }
    if (Object?.keys(formValidation)?.length !== 0) {
      setIsSubmit(false);
      setLoading(false);
    }
  }, [
    isSubmit,
    formValidation,
    groupData.division,
    groupData.sales,
    userDivision,
    userType,
    dispatch,
    getDashboardQueries,
    setIsDivisionGroupClicked,
  ]);



  return (
    <>
      <ModalPopup
        isModalOpen={isModalOpen}
        setIsModalOpen={handleClose}
        title={`Welcome, ${userName}`}
        className="divisionGroup__OuterBox"
        backdrop="static"
        fade={false}
        subtitle={
          isDivisionPreset
            ? `Your Division is ${userDivision} please select the salesgroup code`
            : "Please select division code and salesgroup code"
        }
        data={
          <>
            <Loader loading={loading} />

            {/* Division Code Select (only if no pre-set division) */}
            {!isDivisionPreset && (
              <div className="text-left mb-4">
                <label className="block mb-2 text-sm font-medium text-gray-700">
                  Division Code<span className="text-red-600 ml-0.5">*</span>
                </label>
                <select
                  className="w-full h-[1.7rem] px-2 border border-[#2cc26a] rounded-[2.77px] text-[#2cc26a] text-[9.69px] font-normal focus:outline-none focus:ring-1 focus:ring-[#2cc26a]"
                  value={groupData?.division}
                  name="division"
                  onChange={onChange}
                >
                  <option value="" className="text-gray-400">
                    Division Group
                  </option>
                  {divisionCode?.length > 0 ? (
                    divisionCode?.map((type, index) => (
                      <option key={index} value={type?.division}>
                        {type?.division_name}:{type?.division}
                      </option>
                    ))
                  ) : (
                    <option>Loading...</option>
                  )}
                </select>
                {!groupData?.division && formValidation?.division && (
                  <p className="text-red-600 text-xs mt-1">
                    {formValidation?.division}
                  </p>
                )}
              </div>
            )}

            {/* Sales Group Select */}
            <div className="text-left mb-4">
              <label className="block mb-2 text-sm font-medium text-gray-700 whitespace-nowrap">
                Sales Group<span className="text-red-600 ml-0.5">*</span>
              </label>
              <select
                className="w-full h-[1.7rem] px-2 border border-[#2cc26a] rounded-[2.77px] text-[#2cc26a] text-[9.69px] font-normal focus:outline-none focus:ring-1 focus:ring-[#2cc26a]"
                value={groupData?.sales}
                name="sales"
                onChange={onChange}
              >
                <option value="" className="text-gray-400">
                  Sales Group
                </option>
                {salesCode?.length > 0 ? (
                  salesCode?.map((code, index) => (
                    <option key={index} value={code?.sales_group_code_c}>
                      {code?.sales_group_code_c}
                      {code?.sales_group_name &&
                        ` : ${code?.sales_group_name}`}
                    </option>
                  ))
                ) : (
                  <option>Loading...</option>
                )}
              </select>
              {!groupData?.sales && formValidation?.sales && (
                <p className="text-red-600 text-xs mt-1">
                  {formValidation?.sales}
                </p>
              )}
            </div>

            {/* Submit Button */}
            <div className="flex justify-center mt-6">
              <button
                onClick={onSubmit}
                className="w-[4.5rem] h-[1.7rem] bg-[#3901ff] text-white text-[11.5px] rounded hover:bg-[#2d00cc] transition-colors border-0"
                type="submit"
              >
                Submit
              </button>
            </div>
          </>
        }
      />
    </>
  );
};

export default DivisionGroup;
